import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Target, Eye, Users, Award } from 'lucide-react';

const CompanyPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-fac-dark pt-16">
      {/* Hero */}
      <section className="py-20 relative overflow-hidden" data-testid="company-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920&q=80"
            alt="Farm landscape"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-fac-dark via-fac-dark/90 to-fac-dark" />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading font-black text-5xl md:text-6xl text-white tracking-tighter uppercase mb-4">
              {t('company_title')}
            </h1>
            <p className="text-2xl text-fac-green font-chinese">
              FAC 智慧方案
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-fac-paper" data-testid="company-mv">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-fac-card border border-border rounded-lg p-8 cyber-border">
              <div className="w-16 h-16 rounded-lg bg-fac-green/10 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-fac-green" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">
                {t('company_mission')}
              </h2>
              <p className="text-fac-green font-chinese mb-4">使命</p>
              <p className="text-lg text-gray-300 font-chinese">
                {t('company_mission_text')}
              </p>
              <p className="text-lg text-muted-foreground font-chinese mt-2">
                以智慧科技幫助農民輕鬆管理用水
              </p>
            </div>

            {/* Vision */}
            <div className="bg-fac-card border border-border rounded-lg p-8 cyber-border">
              <div className="w-16 h-16 rounded-lg bg-fac-green/10 flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-fac-green" />
              </div>
              <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">
                {t('company_vision')}
              </h2>
              <p className="text-fac-green font-chinese mb-4">願景</p>
              <p className="text-lg text-gray-300 font-chinese">
                {t('company_vision_text')}
              </p>
              <p className="text-lg text-muted-foreground font-chinese mt-2">
                讓每個農場都能享受智慧灌溉
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-20 bg-fac-dark" data-testid="company-about">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-4xl text-white tracking-tighter uppercase mb-6">
                ABOUT US
                <span className="block text-fac-green font-chinese text-2xl mt-2">
                  關於我們
                </span>
              </h2>
              <div className="space-y-4 text-gray-300 font-chinese">
                <p>
                  FAC Solutions is an agricultural technology company focused on making 
                  smart irrigation accessible to every farmer.
                </p>
                <p className="text-muted-foreground">
                  FAC 智慧方案是一家農業科技公司，致力於讓每位農民都能使用智慧灌溉技術。
                </p>
                <p>
                  We understand the challenges farmers face - unpredictable weather, water scarcity, 
                  and the need for efficient resource management. Our smart valve system is designed 
                  to address these challenges with simple, reliable technology.
                </p>
                <p className="text-muted-foreground">
                  我們了解農民面臨的挑戰——多變的天氣、水資源短缺，以及對高效資源管理的需求。
                  我們的智慧閥門系統正是為了以簡單可靠的技術解決這些挑戰而設計。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-fac-card border border-border rounded-lg p-6 text-center">
                <Users className="w-8 h-8 text-fac-green mx-auto mb-3" />
                <p className="font-heading font-bold text-3xl text-white">500+</p>
                <p className="text-muted-foreground font-chinese text-sm">Active Users / 活躍用戶</p>
              </div>
              <div className="bg-fac-card border border-border rounded-lg p-6 text-center">
                <Award className="w-8 h-8 text-fac-green mx-auto mb-3" />
                <p className="font-heading font-bold text-3xl text-white">1000+</p>
                <p className="text-muted-foreground font-chinese text-sm">Devices Deployed / 已部署設備</p>
              </div>
              <div className="bg-fac-card border border-border rounded-lg p-6 text-center col-span-2">
                <p className="font-heading font-bold text-3xl text-white">2+ Years</p>
                <p className="text-muted-foreground font-chinese text-sm">Of Reliable Service / 可靠服務</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="py-20 bg-fac-paper" data-testid="company-location">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading font-black text-4xl text-white tracking-tighter uppercase mb-4">
            SERVING FARMERS WORLDWIDE
          </h2>
          <p className="text-xl text-fac-green font-chinese mb-8">
            服務全球農民
          </p>
          <p className="text-lg text-gray-300 font-chinese">
            We work closely with farming communities around the world to deliver smart irrigation solutions.
            <br />
            我們與世界各地的農業社區緊密合作，提供智慧灌溉解決方案。
          </p>
        </div>
      </section>
    </div>
  );
};

export default CompanyPage;
